import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Accordion, AccordionSummary, AccordionDetails, Typography, IconButton } from '@mui/material';
import COLOR from 'utils/constants/color';
import { CloudDownload, ExpandMore } from '@mui/icons-material';
import styled from 'styled-components';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'utils/dayjsConfig';
import { getSemesterDateRange, generateDateRanges, parseDateRangeValue, areDateRangesEqual } from 'utils/methods/gradebookDateRange';
import {
  filterGradebookApiResultRows,
  filterGradebookStudentsByActiveStatus,
  getFilteredRosterForGradebookDownload,
  resolveGradebookRosterRaw
} from 'utils/methods/gradebookStudentActiveFilter';
import { handleGetGradebookData, handlelistDownloads } from 'store/exams/thunks';
import { makeStylesWithTheme } from 'utils/styles/mui';
import { useUserClasses, useUsersByClass } from 'store/userClasses/selectors';
import { useCurrentClass, useCurrentClassBook } from 'store/classes/selectors';
import { useUserAllowedBooks } from 'store/books/selectors';
import { useSchoologyGradeCategory } from 'store/schoologyauth/selectors';
import Loading from './Loading';
import MultiClassSelecter from './MultiClassSelecter';
import MultiStudentSelecter from './MultiStudentSelector';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  MenuItem,
  Modal,
  Button as MtBtn,
  Select
} from '@mui/material';
import { map } from "lodash";
import { useAlert } from './UI/alertSakeBar/alertProvider';
import { useParentOrgId } from 'store/organizations/selectors';
import SentryWrap from 'components/Sentry';
import { getCurrentSameDateRange } from 'utils/methods/math';
import { downloadGradebookMatrixCsv, downloadGradebookMatrixExcel } from 'utils/gradebookMatrixExcelExport';

const GRADE_TYPE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'graded', value: 'graded' },
  { label: 'un-graded', value: 'ungraded' }
];

const GRADEBOOK_DOWNLOAD_FETCH_ACTIVE_INACTIVE = 'active';

const DATE_RANGE_SELECT_ALL = 'all';

const sanitizeGradebookExportBasename = (raw) => {
  let s = String(raw ?? '')
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  if (s.endsWith('-')) {
    s = s.replace(/-+$/, '').trim();
  }
  const clipped = s.slice(0, 180);
  return clipped.length > 0 ? clipped : 'gradebook';
};

const buildGradebookExportFileName = (baseLabel, extension) =>
  `${sanitizeGradebookExportBasename(baseLabel)}.${extension}`;

const useStyles = makeStylesWithTheme((theme) => ({
  accordion: {
    marginBottom: '2px',
    maxHeight: 440,
    display: 'flex',
    flexDirection: 'column',
    width: "100%",
    overflow: "hidden"
  },
  accordionSummary: {
    backgroundColor: theme.palette?.primary?.main,
    color: theme.palette?.primary?.contrastText,
    maxHeight: 40,
    alignItems: 'center',
    display: 'flex',
    justifyContent: "center",
  },
  accordionDetails: {
    flexDirection: 'column',
    maxHeight: 400,
    overflowY: 'auto',
  },
  textWrapper: {
    paddingBottom: 8,
    paddingTop: 8,
    borderTop: "0.1px solid",
    borderBottom: "0.1px solid"
  },
  formControl: {
    '& .MuiOutlinedInput-notchedOutline legend': {
      width: 'auto !important',
      padding: '0 18px'
    }
  }
}));

const DownloadAccordion = ({ currentClass }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const dispatch = useDispatch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const parentOrgId = useParentOrgId();

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await dispatch(handlelistDownloads({
        class_id: currentClass.id,
        parent_org_id: parentOrgId
      }));
      if (Array.isArray(response)) {
        setItems(response);
      }
    } catch (error) {
      Sentry.captureException(`211 Error in DownloadAccordion: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (expanded) {
      fetchItems();
    }
  }, [expanded]);

  useEffect(() => {
    fetchItems();
  }, []);

  const whiteColorStyle = { color: "#fff" };

  return (
    <Accordion className={classes.accordion} expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMore style={whiteColorStyle} />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography style={{ display: "flex", alignItems: "center" }}>
          Downloads {loading && <Loading size={14} />}
        </Typography>
        <IconButton onClick={(e) => e.stopPropagation()} edge="end" color="inherit" style={whiteColorStyle}>
          <CloudDownload style={whiteColorStyle} />
        </IconButton>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {items.map((item) => (
          <Typography key={item.id} component="div" style={{ width: "100%" }} className={classes.textWrapper}>
            <a style={{ width: "100%" }} href={item.url} download>{item.title}</a>
          </Typography>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};


const GradeTypeSelector = ({ onSelect }) => {
  const classes = useStyles();
  const [selectedGradeType, setSelectedGradeType] = useState('all');

  const handleChange = (event) => {
    const value = event.target.value;
    setSelectedGradeType(value);
    onSelect(value);
  };

  return (
    <FormControl variant="outlined" className={classes.formControl} fullWidth style={{ marginTop: 16 }}>
      <InputLabel>Select grade type</InputLabel>
      <Select
        value={selectedGradeType}
        onChange={handleChange}
        label="Select grade type"
      >
        {GRADE_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export const DateRangeDropdown = ({ onDatePicked, activeSem, parentOrgId, value: controlledValue, dateOnly = false }) => {
  const semDateRange = useMemo(() => {
    return activeSem ? getSemesterDateRange(activeSem, parentOrgId) : getSemesterDateRange(null, parentOrgId);
  }, [activeSem, parentOrgId]);

  const defaultSemRange = useMemo(() => {
    return `${semDateRange.from_date}/${semDateRange.end_date}`;
  }, [semDateRange.from_date, semDateRange.end_date]);

  const presetDateRanges = useMemo(
    () => [{ label: 'All', value: DATE_RANGE_SELECT_ALL }, ...generateDateRanges(activeSem, parentOrgId)],
    [activeSem, parentOrgId],
  );

  const [selectedDateRange, setSelectedDateRange] = useState(() => defaultSemRange);
  const effectiveValue = controlledValue !== undefined && controlledValue !== null
    ? (controlledValue === '' ? DATE_RANGE_SELECT_ALL : controlledValue)
    : (selectedDateRange === '' ? DATE_RANGE_SELECT_ALL : selectedDateRange);

  const resolvedSelectValue = useMemo(() => {
    if (effectiveValue === '' || effectiveValue === DATE_RANGE_SELECT_ALL) return DATE_RANGE_SELECT_ALL;
    const found = presetDateRanges.find((r) => r.value === effectiveValue || areDateRangesEqual(r.value, effectiveValue));
    if (found) return found.value;
    return 'custom';
  }, [effectiveValue, presetDateRanges]);

  const [open, setOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (controlledValue != null) {
      setSelectedDateRange(controlledValue);
    }
  }, [controlledValue]);

  const handleChange = useCallback((event) => {
    const value = event.target.value;
    if (value === 'custom') {
      setDateError('');
      const currentValue = controlledValue !== undefined && controlledValue !== null ? controlledValue : selectedDateRange;
      const isCustomRange = currentValue && currentValue !== DATE_RANGE_SELECT_ALL && currentValue !== '' &&
        !presetDateRanges.find((r) => r.value === currentValue || areDateRangesEqual(r.value, currentValue));
      if (isCustomRange) {
        const parsed = parseDateRangeValue(currentValue);
        if (parsed) {
          setCustomStartDate(parsed.start);
          setCustomEndDate(parsed.end);
        }
      }
      setOpen(true);
    } else {
      const valueToStore = value === DATE_RANGE_SELECT_ALL ? '' : value;
      setSelectedDateRange(valueToStore);
      onDatePicked(valueToStore);
    }
  }, [onDatePicked, controlledValue, selectedDateRange, presetDateRanges]);

  const handleStartDateChange = useCallback((newDate) => {
    if (!newDate) return;
    const startDate = dayjs(newDate);
    setCustomStartDate(startDate.toDate());
    setDateError('');
    if (customEndDate) {
      const endDate = dayjs(customEndDate);
      if (startDate.isAfter(endDate)) {
        setCustomEndDate(startDate.toDate());
      }
    }
  }, [customEndDate]);

  const handleEndDateChange = useCallback((newDate) => {
    if (!newDate) return;
    const endDate = dayjs(newDate);
    setCustomEndDate(endDate.toDate());
    setDateError('');
    if (customStartDate) {
      const startDate = dayjs(customStartDate);
      if (endDate.isBefore(startDate)) {
        setDateError('End date cannot be less than start date');
      } else {
        setDateError('');
      }
    }
  }, [customStartDate]);

  const handleCustomDateChange = useCallback(() => {
    if (!customStartDate || !customEndDate) {
      setDateError('Please select both start and end dates');
      return;
    }
    
    const startDate = dayjs(customStartDate);
    const endDate = dayjs(customEndDate);
    
    if (endDate.isBefore(startDate)) {
      setDateError('End date cannot be less than start date');
      return;
    }
    
    setDateError('');
    const startForRange = dateOnly ? startDate.startOf('day').toDate() : customStartDate;
    const endForRange = dateOnly ? endDate.endOf('day').toDate() : customEndDate;
    const customRange = `${dayjs(startForRange).toISOString()}/${dayjs(endForRange).toISOString()}`;
    setSelectedDateRange(customRange);
    onDatePicked(customRange);
    setOpen(false);
    setDateError('');
  }, [customStartDate, customEndDate, onDatePicked, dateOnly]);

  const getDisplayValue = useCallback((chosen) => {
    if (chosen === 'custom') return 'Custom date range';
    if (chosen == null || chosen === '' || chosen === DATE_RANGE_SELECT_ALL) return 'All';
    const range = presetDateRanges.find((r) => r.value === chosen || areDateRangesEqual(r.value, chosen));
    return range ? range.label : 'Custom date range';
  }, [presetDateRanges]);

  return (
    <>
      <FormControl variant="outlined" fullWidth>
        <InputLabel>Date range</InputLabel>
        <Select
          value={resolvedSelectValue}
          onChange={handleChange}
          label="Date range"
          renderValue={() => getDisplayValue(resolvedSelectValue)}
        >
          {presetDateRanges.map((range, index) => (
            <MenuItem key={index} value={range.value}>
              {range.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">Custom date range</MenuItem>
        </Select>
      </FormControl>
      <Dialog 
        open={open} 
        onClose={() => {
          setOpen(false);
          setDateError('');
        }}
        maxWidth="sm"
        fullWidth
        disableScrollLock={true}
        PaperProps={{
          style: {
            maxHeight: '90vh',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle>Custom Date Range</DialogTitle>
        <DialogContent style={{ overflowY: 'auto', overflowX: 'hidden', paddingBottom: '12px', paddingTop: '12px' }}>
          <div style={{ marginBottom: 16, width: '100%' }}>
            {dateOnly ? (
              <DatePicker
                label="Start Date"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true
                  },
                  popper: {
                    disablePortal: false,
                    placement: 'bottom-start',
                    sx: { zIndex: 1400 },
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                          padding: 8,
                          altAxis: true,
                        },
                      },
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
                        },
                      },
                    ],
                  }
                }}
                value={customStartDate ? dayjs(customStartDate) : null}
                format="MM-DD-YYYY"
                onChange={handleStartDateChange}
              />
            ) : (
              <DateTimePicker
                label="Start Date"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true
                  },
                  popper: {
                    disablePortal: false,
                    placement: 'bottom-start',
                    sx: { zIndex: 1400 },
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: {
                          boundary: 'viewport',
                          padding: 8,
                          altAxis: true,
                        },
                      },
                      {
                        name: 'flip',
                        options: {
                          fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
                        },
                      },
                    ],
                  },
                  dialog: {
                    sx: { zIndex: 9999 },
                  },
                }}
                value={customStartDate ? dayjs(customStartDate) : null}
                format="MM-DD-YYYY hh:mm a"
                onChange={handleStartDateChange}
              />
            )}
          </div>
          {dateOnly ? (
            <DatePicker
              label="End Date"
              minDate={customStartDate ? dayjs(customStartDate) : undefined}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                  error: !!dateError,
                  helperText: dateError
                },
                popper: {
                  disablePortal: false,
                  placement: 'bottom-start',
                  sx: { zIndex: 1400 },
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport',
                        padding: 8,
                        altAxis: true,
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
                      },
                    },
                  ],
                }
              }}
              value={customEndDate ? dayjs(customEndDate) : null}
              format="MM-DD-YYYY"
              onChange={handleEndDateChange}
            />
          ) : (
            <DateTimePicker
              label="End Date"
              minDate={customStartDate ? dayjs(customStartDate) : undefined}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                  error: !!dateError,
                  helperText: dateError
                },
                popper: {
                  disablePortal: false,
                  placement: 'bottom-start',
                  sx: { zIndex: 1400 },
                  modifiers: [
                    {
                      name: 'preventOverflow',
                      options: {
                        boundary: 'viewport',
                        padding: 8,
                        altAxis: true,
                      },
                    },
                    {
                      name: 'flip',
                      options: {
                        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'],
                      },
                    },
                  ],
                },
                dialog: {
                  sx: { zIndex: 9999 },
                },
              }}
              format="MM-DD-YYYY hh:mm a"
              value={customEndDate ? dayjs(customEndDate) : null}
              onChange={handleEndDateChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <MtBtn onClick={() => {
            setOpen(false);
            setDateError('');
          }} color="primary">
            Cancel
          </MtBtn>
          <MtBtn onClick={handleCustomDateChange} color="primary" disabled={!!dateError}>
            Apply
          </MtBtn>
        </DialogActions>
      </Dialog>
    </>
  );
};






const Button = styled.button`
  background-color: transparent;
  border: none;
  color: ${COLOR.green200};
  cursor: pointer;
  outline: none;
  transition: all 300ms ease;

  &:hover {
    color: ${COLOR.green}
  }
`;

const ModalContent = styled.div`
  align-items: center;
  background-color: ${COLOR.white};
  border-radius: 3px;
  display: inline-flex;
  flex-direction: column;
  left: 50%;
  max-width: 320px;
  padding: 16px;
  position: absolute;
  text-align: center;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const ModalRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 16px;
  width: 100%;
`;

const GradeDownloader = forwardRef(({ 
  scale_1_5, 
  gradebook_a_f, 
  sortBy, 
  datePrefrences, 
  activeSem, 
  orderBy, 
  activeInactive = 'active',
  classUsers = [],
  gradeTableDateRange = '',
}, ref) => {
  const showAlert = useAlert();
  const dispatch = useDispatch();
  const currentClass = useCurrentClass();
  const userClasses = useUserClasses();
  const activeClasses = useMemo(() => {
    return userClasses.filter(c => !c.archived && c.userclass?.active);
  }, [userClasses]);
  const usersByClass = useUsersByClass();
  const [, , schoologyAllClassGradeCategory] = useSchoologyGradeCategory();
  const parentOrgId = useParentOrgId();
  const allowedBooks = useCurrentClassBook();
  const userAllowedBooks = useUserAllowedBooks();

  const semDateRange = useMemo(() => {
    return activeSem ? getSemesterDateRange(activeSem, parentOrgId) : getCurrentSameDateRange(parentOrgId);
  }, [activeSem, parentOrgId]);
  
  const currentSemForComponent = useMemo(() => {
    return `${semDateRange.from_date}/${semDateRange.end_date}`;
  }, [semDateRange.from_date, semDateRange.end_date]);
  
  const [gradePopulateModal, setGradePopulateModal] = useState(false);
  const [loader, setLoading] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => currentSemForComponent);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [fetchTo, setFetchTo] = useState("all");
  const [includeTbg, setIncludeTbg] = useState(false);
  const [selectedClass, setSelectedClass] = useState([]);

  useEffect(() => {
    if (gradePopulateModal) {
      setSelectedDate(currentSemForComponent);
      setSelectedStudents([]);
      setFetchTo("all");
      setIncludeTbg(false);
    }
  }, [gradePopulateModal, currentSemForComponent]);

  useEffect(() => {
    if (currentClass && gradePopulateModal) {
      setSelectedClass([currentClass]);
    }
  }, [currentClass, gradePopulateModal]);

  const onClosePopulateGrade = useCallback(() => {
    setGradePopulateModal(false);
  }, []);

  const onDatePicked = useCallback((value) => {
    setSelectedDate(value);
  }, []);

  const getDateList = useCallback((data, datePrefrences) => {
    let dateList = data.date_list || [];
    if (datePrefrences === 'start_date' && data.start_dates) {
      dateList = data.start_dates.map((d, i) => d || data.date_list[i] || '');
    } else if (datePrefrences === 'late_submission' && data.late_dates) {
      dateList = data.late_dates.map((d, i) => d || data.date_list[i] || '');
    }
    return dateList;
  }, []);

  const createHeaderRows = useCallback((bookNames, assignmentNames, dateList, maxColumns) => {
    const columnNumberingRow = Array.from({ length: maxColumns + 5 }, (_, i) => i.toString());
    const imageRow = Array.from({ length: maxColumns + 5 }, () => '');
    const booksRow = ['', '', '', '', 'Books', ...bookNames];
    const assignmentsRow = ['', '', '', '', 'assignment', ...assignmentNames];
    const studentInfoRow = ['Students', 'username', 'studentId', 'password', 'average', ...dateList];
    
    while (studentInfoRow.length < maxColumns + 5) {
      studentInfoRow.push('');
    }
    
    return [columnNumberingRow, imageRow, booksRow, assignmentsRow, studentInfoRow];
  }, []);

  const extractStudentInfoFromArray = useCallback((student) => {
    return {
      name: student[0] || '',
      username: student[1] || '',
      studentId: student[2] || '',
      password: student[3] || '',
      average: student[4] !== null && student[4] !== undefined ? student[4] : '',
      grades: student.slice(5)
    };
  }, []);

  const extractStudentInfoFromObject = useCallback((student) => {
    let name = '';
    if (student.name) {
      name = student.name;
    } else if (student.firstName && student.lastName) {
      name = `${student.firstName} ${student.lastName}`;
    } else if (student.firstName) {
      name = student.firstName;
    } else if (student.username) {
      name = student.username;
    }
    
    return {
      name,
      username: student.username || '',
      studentId: student.studentId || student.id || '',
      password: student.password || '',
      average: ''
    };
  }, []);

  const getGradeValue = useCallback((student, index, bookNames, assignmentNames) => {
    if (student.grades && Array.isArray(student.grades)) {
      return student.grades[index] || '';
    }
    if (student.scores && Array.isArray(student.scores)) {
      return student.scores[index] || '';
    }
    if (student.submissions && Array.isArray(student.submissions)) {
      return student.submissions[index] || '';
    }
    if (student[`grade_${index}`]) {
      return student[`grade_${index}`];
    }
    if (student[`score_${index}`]) {
      return student[`score_${index}`];
    }
    if (assignmentNames[index] && student[assignmentNames[index]]) {
      return student[assignmentNames[index]];
    }
    if (bookNames[index] && student[bookNames[index]]) {
      return student[bookNames[index]];
    }
    return '';
  }, []);

  const processStudentData = useCallback((student, bookNames, assignmentNames) => {
    let studentInfo;
    let grades = [];
    
    if (Array.isArray(student)) {
      const extracted = extractStudentInfoFromArray(student);
      studentInfo = extracted;
      grades = extracted.grades;
    } else {
      studentInfo = extractStudentInfoFromObject(student);
      grades = bookNames.map((_, index) => getGradeValue(student, index, bookNames, assignmentNames));
    }
    
    return [
      studentInfo.name,
      studentInfo.username,
      studentInfo.studentId,
      studentInfo.password,
      studentInfo.average,
      ...grades
    ];
  }, [extractStudentInfoFromArray, extractStudentInfoFromObject, getGradeValue]);

  const createEmptyDataRows = useCallback(() => {
    return [
      ['', '', '', '', '', '', '', ''],
      ['', '', '', '', 'Books', 'Literacy with Purpose', 'Informate', 'Anécdotas Inicial'],
      ['', '', '', '', 'assignment', '255 | Pg.421', '255 | Pg.511', '41 | Pg.2129'],
      ['Students', 'username', 'studentId', 'password', 'average', 'Aug 30', 'Aug 29', 'Aug 29']
    ];
  }, []);

  const processGradebookData = useCallback((data, datePrefrences) => {
    if (!data || !data.result) {
      return createEmptyDataRows();
    }

    const bookNames = data.book_name || [];
    const assignmentNames = data.assignment_name || [];
    const dateList = getDateList(data, datePrefrences);
    const userData = data.result || [];
    const maxColumns = Math.max(bookNames.length, assignmentNames.length, dateList.length);
    
    const headerRows = createHeaderRows(bookNames, assignmentNames, dateList, maxColumns);
    const studentRows = userData.map(student => processStudentData(student, bookNames, assignmentNames));
    
    return [...headerRows, ...studentRows];
  }, [getDateList, createHeaderRows, processStudentData, createEmptyDataRows]);

  const generateStyledXLSX = useCallback(async (data, fileName) => {
    try {
      await downloadGradebookMatrixExcel(data, fileName);
    } catch (error) {
      Sentry.captureException(`Error in generateStyledXLSX: ${error}`);
    }
  }, []);

  const handleDownloadSpecificAssignment = useCallback(async (assignmentId, type, includeTbgValue = false) => {
    if (!selectedDate) return;
    
    setLoading(type);
    showAlert("Downloading assignment grades...", "info");
    
    if (!currentClass) {
      showAlert("Please select a class", "error");
      setLoading(null);
      return;
    }
    
    await Promise.allSettled(map([currentClass], async (userClass) => {
      const schoologyGradeCategory = schoologyAllClassGradeCategory?.[userClass.sectionId] || [];
      
      try {
        const [fromDate, endDate] = selectedDate.split('/');
        
        const assignmentRosterForFilter = getFilteredRosterForGradebookDownload(
          userClass.id,
          activeInactive,
          [],
          currentClass,
          classUsers,
          usersByClass
        );

        const data = await dispatch(handleGetGradebookData({
          from_date: fromDate,
          end_date: endDate,
          class_id: userClass.id,
          students: [],
          scale_1_5,
          gradebook_a_f,
          schoologyGradeCategory,
          fetch_to: fetchTo,
          sortBy,
          orderBy,
          activeInactive: GRADEBOOK_DOWNLOAD_FETCH_ACTIVE_INACTIVE,
          datePrefrences,
          include_tbg: includeTbgValue,
          semester: activeSem,
          assignment_id: assignmentId,
          allowed_books: allowedBooks,
          resource_allowed: userAllowedBooks?.other_permissions?.resourses,
          parent_org_id: parentOrgId,
          grade_table_date_range: gradeTableDateRange,
        }));

        const assignmentFilteredResult = filterGradebookApiResultRows(
          data?.result,
          assignmentRosterForFilter,
          { treatEmptyAllowedAsNoRows: true }
        );
        const assignmentDataForProcess = data ? { ...data, result: assignmentFilteredResult } : data;

        const processedData = processGradebookData(assignmentDataForProcess, datePrefrences);
        const isCsv = type && String(type).toUpperCase() === 'CSV';
        const fileName = buildGradebookExportFileName(
          `${userClass.displayName}-Assignment-${assignmentId}`,
          isCsv ? 'csv' : 'xlsx',
        );
        if (isCsv) {
          await downloadGradebookMatrixCsv(processedData, fileName);
        } else {
          await generateStyledXLSX(processedData, fileName);
        }
      } catch (error) {
        Sentry.captureException(`Error in handleDownloadSpecificAssignment: ${error}`);
        showAlert("Error downloading assignment grades", "error");
      }
    })).then(() => {
      showAlert("Downloaded successfully", "success");
      setLoading(null);
    }).catch(() => {
      setLoading(null);
    });
  }, [
    selectedDate, 
    currentClass, 
    schoologyAllClassGradeCategory, 
    dispatch, 
    scale_1_5, 
    gradebook_a_f, 
    fetchTo, 
    sortBy, 
    orderBy, 
    activeInactive, 
    datePrefrences, 
    includeTbg, 
    activeSem, 
    showAlert, 
    processGradebookData, 
    generateStyledXLSX,
    downloadGradebookMatrixCsv,
    usersByClass,
    classUsers,
    allowedBooks,
    userAllowedBooks,
    parentOrgId,
    gradeTableDateRange
  ]);

  useImperativeHandle(ref, () => ({
    open: () => {
      setGradePopulateModal(true);
    },
    downloadSpecificAssignment: (assignmentId, type, includeTbgValue = false) => {
      handleDownloadSpecificAssignment(assignmentId, type, includeTbgValue);
    }
  }), [handleDownloadSpecificAssignment]);

  const handleFetchData = useCallback(async (type) => {
    if (!selectedDate) return;
    
    setLoading(type);
    const classesList = selectedClass.length > 0 ? selectedClass : activeClasses;
    
    await Promise.allSettled(map(classesList, async (userClass) => {
      const schoologyGradeCategory = schoologyAllClassGradeCategory?.[userClass.sectionId] || [];
      
      try {
        const [fromDate, endDate] = selectedDate.split('/');

        const isSingleClassDownload = classesList.length === 1;
        const rosterForFilter = isSingleClassDownload
          ? getFilteredRosterForGradebookDownload(
            userClass.id,
            activeInactive,
            selectedStudents,
            currentClass,
            classUsers,
            usersByClass
          )
          : [];

        const data = await dispatch(handleGetGradebookData({
          from_date: fromDate,
          end_date: endDate,
          class_id: userClass.id,
          students: [],
          scale_1_5,
          gradebook_a_f,
          schoologyGradeCategory,
          fetch_to: fetchTo,
          sortBy,
          orderBy,
          activeInactive: GRADEBOOK_DOWNLOAD_FETCH_ACTIVE_INACTIVE,
          datePrefrences,
          include_tbg: includeTbg,
          semester: activeSem,
          allowed_books: allowedBooks,
          resource_allowed: userAllowedBooks?.other_permissions?.resourses,
          parent_org_id: parentOrgId,
          grade_table_date_range: gradeTableDateRange,
        }));

        const bulkFilteredResult = isSingleClassDownload
          ? filterGradebookApiResultRows(
            data?.result,
            rosterForFilter,
            { treatEmptyAllowedAsNoRows: true }
          )
          : (data?.result || []);
        const dataForProcess = data ? { ...data, result: bulkFilteredResult } : data;

        const finalData = processGradebookData(dataForProcess, datePrefrences);
        const isCsv = type && String(type).toUpperCase() === 'CSV';
        const fileName = buildGradebookExportFileName(userClass.displayName, isCsv ? 'csv' : 'xlsx');
        if (isCsv) {
          await downloadGradebookMatrixCsv(finalData, fileName);
        } else {
          await generateStyledXLSX(finalData, fileName);
        }
      } catch (error) {
        Sentry.captureException(`212 Error in handleFetchData: ${error}`);
      }
    })).then(() => {
      showAlert("Downloaded successfully", "success");
    });
    
    setLoading(null);
    onClosePopulateGrade();
  }, [selectedDate, selectedClass, activeClasses, selectedStudents, schoologyAllClassGradeCategory, dispatch, scale_1_5, gradebook_a_f, fetchTo, sortBy, orderBy, activeInactive, datePrefrences, includeTbg, activeSem, showAlert, onClosePopulateGrade, processGradebookData, generateStyledXLSX, downloadGradebookMatrixCsv, usersByClass, currentClass, classUsers, allowedBooks, userAllowedBooks, parentOrgId, gradeTableDateRange]);
  
  const getClassStudents = useMemo(() => {
    if (selectedClass.length !== 1) {
      return [];
    }
    const classId = selectedClass[0].id;
    const raw = resolveGradebookRosterRaw(classId, currentClass, classUsers, usersByClass);
    return filterGradebookStudentsByActiveStatus(raw, activeInactive);
  }, [selectedClass, usersByClass, currentClass, classUsers, activeInactive]);

  const handleClassSelect = useCallback((value) => {
    setSelectedClass(value);
    setSelectedStudents([]);
  }, []);

  const handleDownloadClick = useCallback((type) => {
    handleFetchData(type);
  }, [handleFetchData]);

  const handleIncludeTbgChange = useCallback((e) => {
    setIncludeTbg(e.target.checked);
  }, []);

  const handleXLSXDownload = useCallback(() => {
    handleDownloadClick("XLSX");
  }, [handleDownloadClick]);

  const handleCSVDownload = useCallback(() => {
    handleDownloadClick("CSV");
  }, [handleDownloadClick]);

  const handleOpenModal = useCallback(() => {
    setGradePopulateModal(true);
  }, []);

  const modalRowStyle = useMemo(() => ({ marginTop: "16px" }), []);
  const includeTbgLabelStyle = useMemo(() => ({ alignSelf: 'flex-start', marginTop: 8 }), []);
  const buttonMarginStyle = useMemo(() => ({ marginRight: '4px' }), []);
  const downloadButtonStyle = useMemo(() => ({ marginRight: '16px' }), []);

  return (
    <SentryWrap>
      <Modal open={gradePopulateModal} onClose={onClosePopulateGrade}>
        <ModalContent>
          <DownloadAccordion currentClass={currentClass} />
          {gradePopulateModal && (
            <DateRangeDropdown 
              key={`${gradePopulateModal}-${currentSemForComponent}`}
              onDatePicked={onDatePicked} 
              activeSem={activeSem}
              parentOrgId={parentOrgId}
            />
          )}
          {gradePopulateModal && selectedClass.length === 1 && (
            <MultiStudentSelecter
              options={getClassStudents}
              onSelect={setSelectedStudents}
              defaultValue={selectedStudents}
              wrapperStyle={modalRowStyle}
            />
          )}
          {gradePopulateModal && (
            <MultiClassSelecter
              options={activeClasses}
              onSelect={handleClassSelect}
              defaultValue={selectedClass}
              wrapperStyle={modalRowStyle}
            />
          )}
          {gradePopulateModal && (
            <GradeTypeSelector onSelect={setFetchTo} />
          )}
          {gradePopulateModal && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeTbg}
                  onChange={handleIncludeTbgChange}
                  color="primary"
                />
              }
              label="Include TBG"
              style={includeTbgLabelStyle}
            />
          )}
          <ModalRow>
            <MtBtn
              color="secondary"
              style={buttonMarginStyle}
              onClick={onClosePopulateGrade}
            >
              Cancel
            </MtBtn>
            <MtBtn
              color="primary"
              style={buttonMarginStyle}
              onClick={handleXLSXDownload}
            >
              {loader === "XLSX" ? <Loading size={14} /> : <DownloadOutlined style={buttonMarginStyle} />}
              XLSX
            </MtBtn>
            <MtBtn
              color="primary"
              style={buttonMarginStyle}
              onClick={handleCSVDownload}
            >
              {loader === "CSV" ? <Loading size={14} /> : <DownloadOutlined style={buttonMarginStyle} />}
              CSV
            </MtBtn>
          </ModalRow>
        </ModalContent>
      </Modal>
      <Button onClick={handleOpenModal} style={downloadButtonStyle}>
        <DownloadOutlined style={buttonMarginStyle} />
        Download
      </Button>
    </SentryWrap>
  );
});

export default GradeDownloader;